import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { ComParcel, ComWarehouse, COM_PARCEL_STATUS_LABELS } from '../../../../interfaces/ComParcel.interfaces';
import { ComParcelEvent } from '../../../../interfaces/ComParcelEvent.interfaces';
import { Tracking } from '../../../../interfaces/Tracking.interfaces';
import { Carriers } from '../../../../interfaces/Carriers.interfaces';
import { Parcels } from '../../../../interfaces/Parcels.interfaces';
import { Dealer } from '../../../../interfaces/waybill.interfaces';
import { DamageManagementService } from '../../../../services/damage-management.service';

/*
 * Tipi della vista "Percorso": gli stessi della tab Instradamento del popup
 * dettaglio collo di /shipment-management (parcel-detail-popup), senza la
 * parte mappa.
 */

/** Punto di transito (magazzino intermedio o dealer di destinazione finale) */
export interface RoutingChainPoint {
  /** Codice magazzino o codice dealer (vuoto se il punto non è definito sulla tappa) */
  code: string;
  name: string;
  kind: 'warehouse' | 'dealer';
  /** Località (città e provincia), se nota */
  place?: string;
  /** Stato della tappa che arriva sul punto (per il punto di partenza, quello della tappa che ne parte) */
  status?: string;
}

/** Tratta tra due punti di transito, con l'azienda trasportatrice */
export interface RoutingChainLeg {
  /** Numero della tappa (parcelComplementarySequence) */
  sequence: number;
  /** Numero progressivo del trasporto (1, 2, 3...): conta solo le tratte disegnate, senza le soste */
  transport: number;
  status: string;
  carrierCode?: string;
  /** Nome dell'azienda trasportatrice (il codice, se il vettore non è in anagrafica) */
  carrierName?: string;
}

/** Gruppo di eventi di danno (DAMAGE): gli eventi dello stesso magazzino registrati entro 6 ore dal primo */
export interface RoutingChainDamage {
  date?: string;
  /** Data dell'ultimo evento del gruppo (solo se il gruppo ha più eventi) */
  endDate?: string;
  count: number;
  /** Chi ha segnalato il danno (più nomi, se diversi) */
  createdBy?: string;
  /** Magazzino dell'evento: valorizzato solo se non è un punto del percorso */
  place?: string;
  /** Id degli eventi del gruppo (parcelEventComplementaryId), per aggiornarli sul backend */
  eventIds: number[];
}

/** Gruppo di eventi di picking (palmare) di un magazzino, registrati entro 6 ore dal primo */
export interface RoutingChainPicking {
  date?: string;
  count: number;
}

/** Nodo del percorso: un punto di transito e la tratta che ne parte verso il successivo */
export interface RoutingChainNode {
  point: RoutingChainPoint;
  leg?: RoutingChainLeg;
  damages: RoutingChainDamage[];
  pickings: RoutingChainPicking[];
}

/** Tappa MISROUTING di una deviazione */
export interface RoutingChainDetourLeg {
  to: string;
  toPlace?: string;
  toCode?: string;
  carrierName: string;
  title: string;
  damages: RoutingChainDamage[];
  pickings: RoutingChainPicking[];
}

/** Deviazione: una o più tappe MISROUTING concatenate su una corsia parallela al percorso */
export interface RoutingChainDetour {
  gridColumn: string;
  lane: number;
  backward: boolean;
  leftAnchored: boolean;
  rightAnchored: boolean;
  startAnchored: boolean;
  endAnchored: boolean;
  startName: string;
  endName: string;
  startPlace?: string;
  endPlace?: string;
  startCode?: string;
  startDamages: RoutingChainDamage[];
  startPickings: RoutingChainPicking[];
  legs: RoutingChainDetourLeg[];
}

/** Riga della tabella degli orari sotto il percorso: una per tappa */
export interface RoutingScheduleRow {
  sequence?: number;
  from: string;
  to: string;
  fromCode?: string;
  toCode?: string;
  damages: RoutingChainDamage[];
  carrierName: string;
  status: string;
  start?: string;
  end?: string;
  misrouting: boolean;
}

/** Finestra entro cui gli eventi di danno (o picking) dello stesso magazzino finiscono nello stesso gruppo */
const DAMAGE_GROUP_WINDOW_MS = 6 * 60 * 60 * 1000;

/**
 * Altezza di base di una corsia di deviazione, altezza di un'etichetta di danno e spazio tra pallino
 * ed etichetta: devono coincidere con --detour-lane-height, --damage-chip-height e --detour-damage-gap del CSS
 */
const ROUTING_DETOUR_LANE_HEIGHT = 104;
const ROUTING_DAMAGE_CHIP_HEIGHT = 52;
const ROUTING_DETOUR_DAMAGE_GAP = 14;
const ROUTING_DAMAGE_CHIP_SPACING = 6;

/**
 * Sezione "Instradamento" del dettaglio pratica danno: la vista "Percorso"
 * (O----T----O) della tab Instradamento del popup dettaglio collo di
 * /shipment-management, senza la mappa. I dati arrivano dall'endpoint
 * dedicato GET /damaged-parcels/{damageId}/routing (tappe, magazzini, picking,
 * eventi e vettori del collo della pratica). Il contenitore (card e
 * intestazione) è della pagina: qui solo il corpo.
 */
@Component({
  selector: 'app-damage-routing',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './damage-routing.component.html',
  styleUrls: ['./damage-routing.component.css']
})
export class DamageRoutingComponent implements OnChanges {
  @Input() damageId = 0;
  /** Collo della pratica (consegna, flag danneggiato) */
  @Input() parcel: Parcels | null = null;
  /** Dealer di destinazione finale */
  @Input() dealer: Dealer | null = null;
  /** True se l'utente può indicare il magazzino del danno (permesso UPDATE sulla funzione damages) */
  @Input() canAssignLocation = false;
  /** Numero di tappe caricate, per il contatore nell'intestazione della card */
  @Output() countChange = new EventEmitter<number>();

  isLoading = false;
  errorMessage = '';
  hasLoaded = false;

  comParcels: ComParcel[] = [];
  pickingEvents: Tracking[] = [];
  parcelEvents: ComParcelEvent[] = [];
  carriers: Carriers[] = [];
  private warehouseMap = new Map<string, ComWarehouse>();

  routingChain: RoutingChainNode[] = [];
  routingChainDetours: RoutingChainDetour[] = [];
  routingChainDetourLanes = 0;
  routingChainDetourLaneHeight = ROUTING_DETOUR_LANE_HEIGHT;
  routingChainGridColumns = '';
  routingChainUnplacedDamages: RoutingChainDamage[] = [];
  routingSchedule: RoutingScheduleRow[] = [];

  /** Codice del magazzino in corso di assegnazione come magazzino del danno ('' se nessuno) */
  assigningLocationCode = '';
  locationError = '';

  constructor(private damageService: DamageManagementService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['damageId']) {
      this.load();
    } else if (changes['parcel'] || changes['dealer']) {
      if (this.comParcels.length > 0) this.buildRoutingChain();
    }
  }

  /** Carica (o ricarica) i dati di instradamento della pratica */
  load(): void {
    if (!this.damageId) {
      this.reset();
      this.countChange.emit(0);
      return;
    }
    this.isLoading    = true;
    this.errorMessage = '';
    this.damageService.getDamageRouting(this.damageId).pipe(
      finalize(() => { this.isLoading = false; this.hasLoaded = true; })
    ).subscribe({
      next: routing => {
        // Ordina per sequenza numerica. Le scansioni fuori percorso (misrouting) non hanno sequenza:
        // restano dopo la tappa che le precede nella risposta (il backend le inserisce in ordine di data)
        const sortKeys = new Map<ComParcel, number>();
        let lastSequence = 0;
        routing.comParcels.forEach(comParcel => {
          if (!this.isOffRouteScan(comParcel)) {
            lastSequence = comParcel.parcelComplementarySequence || 0;
          }
          sortKeys.set(comParcel, lastSequence);
        });
        this.comParcels    = [...routing.comParcels].sort((a, b) => sortKeys.get(a)! - sortKeys.get(b)!);
        this.pickingEvents = routing.pickingEvents;
        this.parcelEvents  = routing.events;
        this.carriers      = routing.carriers;
        this.warehouseMap  = new Map(routing.comWarehouses.map(w => [w.warehouseCode, w]));
        this.buildRoutingChain();
        this.countChange.emit(this.comParcels.length);
      },
      error: (err: Error) => {
        this.reset();
        this.errorMessage = err?.message || 'Errore nel caricamento dei dati di instradamento. Riprova più tardi.';
        this.countChange.emit(0);
      }
    });
  }

  private reset(): void {
    this.comParcels = [];
    this.pickingEvents = [];
    this.parcelEvents = [];
    this.carriers = [];
    this.warehouseMap = new Map();
    this.routingChain = [];
    this.routingChainDetours = [];
    this.routingChainDetourLanes = 0;
    this.routingChainGridColumns = '';
    this.routingChainUnplacedDamages = [];
    this.routingSchedule = [];
  }

  // ─── Dati di contesto ───

  get dealerName(): string {
    return this.dealer?.dealerName || this.dealer?.dealerLegalName || '-';
  }

  /** Collo segnalato come danneggiato: parcelIsDamaged = true oppure 'Y' */
  private isParcelFlaggedDamaged(): boolean {
    const flag = this.parcel?.parcelIsDamaged as unknown;
    return flag === true || (typeof flag === 'string' && flag.trim().toUpperCase() === 'Y');
  }

  /** Collo segnalato come danneggiato, ma senza evento di danno da collocare sul percorso */
  isDamagedWithoutEvent(): boolean {
    return this.isParcelFlaggedDamaged() && this.hasLoaded && !this.parcelEvents.some(e => this.isDamageEvent(e));
  }

  private isDamageEvent(event: ComParcelEvent): boolean {
    return (event.parcelEventComplementaryName || '').trim().toUpperCase() === 'DAMAGE';
  }

  // ─── Magazzino del danno (bottone "Danno avvenuto qui" sotto i magazzini del percorso) ───

  /**
   * True se mostrare, sotto ogni magazzino del percorso, il bottone per indicare che il
   * danno è avvenuto lì: l'utente può operare e ci sono eventi di danno non collocabili
   * sul percorso (magazzino assente o fuori percorso).
   */
  get showLocationButtons(): boolean {
    return this.canAssignLocation && this.routingChainUnplacedDamages.length > 0;
  }

  /**
   * Registra il magazzino del punto come magazzino del danno sugli eventi DAMAGE non
   * collocabili (POST /damaged-parcels/{id}/damage-warehouse), poi ricarica il percorso:
   * il danno compare sul punto scelto.
   */
  assignLocation(node: RoutingChainNode): void {
    const warehouseCode = node.point.kind === 'warehouse' ? node.point.code : '';
    if (!warehouseCode || !this.damageId || this.assigningLocationCode || !this.showLocationButtons) return;

    const eventIds = this.routingChainUnplacedDamages.flatMap(damage => damage.eventIds);
    this.assigningLocationCode = warehouseCode;
    this.locationError = '';
    this.damageService.setDamageWarehouse(this.damageId, warehouseCode, eventIds).pipe(
      finalize(() => { this.assigningLocationCode = ''; })
    ).subscribe({
      next: () => this.load(),
      error: (err: Error) => {
        this.locationError = err?.message || 'Errore nella registrazione del magazzino del danno. Riprova più tardi.';
      }
    });
  }

  getWarehouseName(warehouseCode: string | undefined): string {
    if (!warehouseCode) return '-';
    const warehouse = this.warehouseMap.get(warehouseCode);
    return warehouse?.warehouseName || warehouse?.warehouseShortName || warehouseCode;
  }

  // ─── Costruzione della vista "Percorso" (stessa logica del popup dettaglio collo) ───

  private buildRoutingChain(): void {
    // Le tappe MISROUTING non fanno parte del percorso: corrono parallele ad esso (deviazioni).
    // Senza un percorso su cui affiancarle (meno di due punti) restano tappe come le altre.
    let mainLegs = this.comParcels.filter(comParcel => !this.isMisroutingLeg(comParcel));
    let chain = this.buildRoutingChainNodes(mainLegs);
    if (chain.nodes.length < 2 && mainLegs.length < this.comParcels.length) {
      mainLegs = this.comParcels;
      chain = this.buildRoutingChainNodes(mainLegs);
    }
    const nodes = chain.nodes;

    const schedule: RoutingScheduleRow[] = [];
    const openDetours: {
      start: RoutingChainPoint | null;
      end: RoutingChainPoint | null;
      legs: RoutingChainDetourLeg[];
      reachedIndex: number;
      startIndex?: number;
    }[] = [];
    let reachedIndex = 0;
    let continuesDetour = false;

    this.comParcels.forEach((comParcel, index) => {
      const status = comParcel.parcelComplementaryStatus || '';
      const sequence = comParcel.parcelComplementarySequence || (index + 1);
      const carrierName = this.getCarrierName(this.resolveLegCarrierCode(comParcel));
      const ends = chain.ends.get(comParcel);
      let fromName: string;
      let toName: string;
      let fromPoint: RoutingChainPoint | null;
      let toPoint: RoutingChainPoint | null;

      if (ends) {
        fromName = ends.from.point.name;
        toName = ends.to.point.name;
        fromPoint = ends.from.point;
        toPoint = ends.to.point;
        reachedIndex = nodes.indexOf(ends.to);
        continuesDetour = false;
      } else {
        const from = this.resolveRoutingChainPoint(comParcel, 'from', false);
        const to = this.resolveRoutingChainPoint(comParcel, 'to', false);
        fromName = from?.name || 'Non definito';
        toName = to?.name || 'Non definito';
        fromPoint = from;
        toPoint = to;

        const offRouteScan = this.isOffRouteScan(comParcel);
        const reachedPoint = nodes[reachedIndex].point;

        const leg: RoutingChainDetourLeg = {
          to: toName,
          toPlace: to?.place,
          toCode: to?.kind === 'warehouse' ? to.code : undefined,
          damages: [],
          pickings: [],
          carrierName,
          title: [
            offRouteScan
              ? `Misrouting · ${reachedPoint.name} → ${toName}`
              : `Misrouting · Tappa ${sequence}: ${fromName} → ${toName}`,
            carrierName,
            offRouteScan ? '' : this.getRoutingStatusLabel(status)
          ].filter(part => !!part).join(' · ')
        };
        const previous = openDetours[openDetours.length - 1];
        if (offRouteScan) {
          openDetours.push({ start: reachedPoint, end: to, legs: [leg], reachedIndex, startIndex: reachedIndex });
        } else if (continuesDetour && previous && this.isSameRoutingPoint(previous.end, from)) {
          previous.legs.push(leg);
          previous.end = to;
        } else {
          openDetours.push({ start: from, end: to, legs: [leg], reachedIndex });
        }
        continuesDetour = true;
      }

      schedule.push({
        sequence: this.isOffRouteScan(comParcel) ? undefined : sequence,
        from: fromName,
        to: toName,
        fromCode: fromPoint?.kind === 'warehouse' ? fromPoint.code : undefined,
        toCode: toPoint?.kind === 'warehouse' ? toPoint.code : undefined,
        damages: [],
        carrierName,
        status,
        start: comParcel.parcelComplementaryEventStart,
        end: comParcel.parcelComplementaryEventEnd,
        misrouting: !ends
      });
    });

    const indexOfPoint = (point: RoutingChainPoint | null, fromIndex: number = 0): number =>
      point && point.code
        ? nodes.findIndex((node, i) => i >= fromIndex && this.isSameRoutingPoint(node.point, point))
        : -1;
    const lanes: { left: number; right: number }[][] = [];
    const detours: RoutingChainDetour[] = openDetours.map(detour => {
      let start = detour.startIndex ?? indexOfPoint(detour.start);
      const startAnchored = start >= 0;
      if (!startAnchored) {
        start = detour.reachedIndex;
      }

      let end = indexOfPoint(detour.end, start + 1);
      if (end < 0) {
        end = indexOfPoint(detour.end);
      }
      const endAnchored = end >= 0 && end !== start;
      if (!endAnchored) {
        end = start < nodes.length - 1 ? start + 1 : start - 1;
      }

      const backward = end < start;
      const left = Math.min(start, end);
      const right = Math.max(start, end);

      let lane = lanes.findIndex(spans => spans.every(span => right <= span.left || left >= span.right));
      if (lane < 0) {
        lane = lanes.length;
        lanes.push([]);
      }
      lanes[lane].push({ left, right });

      return {
        gridColumn: `${2 * left + 1} / ${2 * right + 2}`,
        lane,
        backward,
        leftAnchored: backward ? endAnchored : startAnchored,
        rightAnchored: backward ? startAnchored : endAnchored,
        startAnchored,
        endAnchored,
        startName: detour.start?.name || 'Non definito',
        endName: detour.end?.name || 'Non definito',
        startPlace: detour.start?.place,
        endPlace: detour.end?.place,
        startCode: detour.start?.kind === 'warehouse' ? detour.start.code : undefined,
        startDamages: [],
        startPickings: [],
        legs: detour.legs
      };
    });

    const unplacedDamages: RoutingChainDamage[] = [];
    const damageGroups = this.groupDamageEvents();

    const transitRows = schedule.filter(row => !!row.fromCode && row.fromCode === row.toCode);
    damageGroups.forEach(({ warehouseCode, damage }) => {
      const rows = transitRows.filter(row => row.fromCode === warehouseCode);
      const time = new Date(damage.date || '').getTime();
      const row = rows.find(candidate => {
        const start = new Date(candidate.start || '').getTime();
        const end = candidate.end ? new Date(candidate.end).getTime() : Infinity;
        return !isNaN(time) && !isNaN(start) && time >= start && time <= end;
      }) || rows[0];
      row?.damages.push(damage);
    });

    damageGroups.forEach(({ warehouseCode, damage }) => {
      const node = warehouseCode
        ? nodes.find(n => n.point.kind === 'warehouse' && n.point.code === warehouseCode)
        : undefined;

      if (node) {
        node.damages.push(damage);
      } else if (!warehouseCode || !this.placeDamageOnDetour(detours, warehouseCode, damage)) {
        unplacedDamages.push({ ...damage, place: warehouseCode ? this.getWarehouseName(warehouseCode) : undefined });
      }
    });

    this.groupPickingEvents().forEach(({ warehouseCode, picking }) => {
      const node = nodes.find(n => n.point.kind === 'warehouse' && n.point.code === warehouseCode);
      if (node) {
        node.pickings.push(picking);
      } else {
        this.placePickingOnDetour(detours, warehouseCode, picking);
      }
    });

    const maxDamageGroups = detours.reduce((max, detour) =>
      Math.max(max, detour.startDamages.length, ...detour.legs.map(leg => leg.damages.length)), 0);
    this.routingChainDetourLaneHeight = maxDamageGroups > 0
      ? ROUTING_DETOUR_LANE_HEIGHT + ROUTING_DETOUR_DAMAGE_GAP + ROUTING_DAMAGE_CHIP_SPACING
          + maxDamageGroups * ROUTING_DAMAGE_CHIP_HEIGHT + (maxDamageGroups - 1) * ROUTING_DAMAGE_CHIP_SPACING
      : ROUTING_DETOUR_LANE_HEIGHT;

    this.routingChain = nodes;
    this.routingChainDetours = detours;
    this.routingChainDetourLanes = lanes.length;
    this.routingChainGridColumns = nodes
      .map((_, i) => i === 0 ? 'var(--chain-point-width)' : 'minmax(170px, 1fr) var(--chain-point-width)')
      .join(' ');
    this.routingChainUnplacedDamages = unplacedDamages;
    this.routingSchedule = schedule;
  }

  private groupDamageEvents(): { warehouseCode: string; damage: RoutingChainDamage }[] {
    const eventsByWarehouse = new Map<string, ComParcelEvent[]>();
    this.parcelEvents
      .filter(event => this.isDamageEvent(event))
      .sort((a, b) =>
        new Date(a.parcelEventComplementaryCreatedAt || 0).getTime() - new Date(b.parcelEventComplementaryCreatedAt || 0).getTime())
      .forEach(event => {
        const warehouseCode = event.parcelEventComplementaryWarehouseFrom || event.parcelEventComplementaryWarehouseTo || '';
        const events = eventsByWarehouse.get(warehouseCode) || [];
        events.push(event);
        eventsByWarehouse.set(warehouseCode, events);
      });

    const groups: { warehouseCode: string; damage: RoutingChainDamage }[] = [];
    eventsByWarehouse.forEach((events, warehouseCode) => {
      let current: ComParcelEvent[] = [];
      let windowStart = NaN;

      const flush = () => {
        if (current.length === 0) return;
        const first = current[0];
        const last = current[current.length - 1];
        const authors = Array.from(new Set(
          current.map(event => event.parcelEventComplementaryCreatedBy).filter((author): author is string => !!author)));
        groups.push({
          warehouseCode,
          damage: {
            date: first.parcelEventComplementaryCreatedAt,
            endDate: current.length > 1 ? last.parcelEventComplementaryCreatedAt : undefined,
            count: current.length,
            createdBy: authors.join(', ') || undefined,
            eventIds: current
              .map(event => event.parcelEventComplementaryId)
              .filter((id): id is number => id != null)
          }
        });
        current = [];
      };

      events.forEach(event => {
        const time = new Date(event.parcelEventComplementaryCreatedAt || '').getTime();
        const inWindow = current.length > 0
          && (isNaN(time) || isNaN(windowStart) || time - windowStart <= DAMAGE_GROUP_WINDOW_MS);
        if (!inWindow) {
          flush();
          windowStart = time;
        }
        current.push(event);
      });
      flush();
    });

    return groups.sort((a, b) =>
      new Date(a.damage.date || 0).getTime() - new Date(b.damage.date || 0).getTime());
  }

  private groupPickingEvents(): { warehouseCode: string; picking: RoutingChainPicking }[] {
    const sorted = [...this.pickingEvents].sort((a, b) =>
      new Date(a.trackingCreatedAt || 0).getTime() - new Date(b.trackingCreatedAt || 0).getTime());

    const open = new Map<string, { picking: RoutingChainPicking; windowStart: number }>();
    const groups: { warehouseCode: string; picking: RoutingChainPicking }[] = [];

    sorted.forEach(event => {
      const warehouseCode = event.trackingWarehouseCode;
      const time = new Date(event.trackingCreatedAt || '').getTime();
      const current = open.get(warehouseCode);
      if (current && (isNaN(time) || isNaN(current.windowStart) || time - current.windowStart <= DAMAGE_GROUP_WINDOW_MS)) {
        current.picking.count++;
        return;
      }
      const picking: RoutingChainPicking = { date: event.trackingCreatedAt, count: 1 };
      open.set(warehouseCode, { picking, windowStart: time });
      groups.push({ warehouseCode, picking });
    });

    return groups;
  }

  getPickingTitle(picking: RoutingChainPicking): string {
    return picking.count > 1 ? `${picking.count} pickerate con il palmare` : 'Pickerata con il palmare';
  }

  private placeDamageOnDetour(detours: RoutingChainDetour[], warehouseCode: string, damage: RoutingChainDamage): boolean {
    for (const detour of detours) {
      for (let i = 0; i < detour.legs.length; i++) {
        const leg = detour.legs[i];
        const isEnd = i === detour.legs.length - 1;
        if (leg.toCode === warehouseCode && (!isEnd || !detour.endAnchored)) {
          leg.damages.push(damage);
          return true;
        }
      }
      if (!detour.startAnchored && detour.startCode === warehouseCode) {
        detour.startDamages.push(damage);
        return true;
      }
    }
    return false;
  }

  private placePickingOnDetour(detours: RoutingChainDetour[], warehouseCode: string, picking: RoutingChainPicking): void {
    for (const detour of detours) {
      for (let i = 0; i < detour.legs.length; i++) {
        const leg = detour.legs[i];
        const isEnd = i === detour.legs.length - 1;
        if (leg.toCode === warehouseCode && (!isEnd || !detour.endAnchored)) {
          leg.pickings.push(picking);
          return;
        }
      }
      if (!detour.startAnchored && detour.startCode === warehouseCode) {
        detour.startPickings.push(picking);
        return;
      }
    }
  }

  /** Quando finisce il gruppo: solo l'ora se è lo stesso giorno del primo evento, altrimenti data e ora */
  getDamageRangeLabel(damage: RoutingChainDamage): string {
    if (!damage.date || !damage.endDate) return '';
    const sameDay = formatDate(damage.date, 'yyyy-MM-dd', 'en-US') === formatDate(damage.endDate, 'yyyy-MM-dd', 'en-US');
    return '→ ' + formatDate(damage.endDate, sameDay ? 'HH:mm' : 'dd/MM/yyyy HH:mm', 'en-US');
  }

  getDamageTitle(damage: RoutingChainDamage): string {
    return [
      damage.count > 1 ? `${damage.count} eventi di danno` : 'Evento di danno',
      damage.createdBy ? `Segnalato da ${damage.createdBy}` : ''
    ].filter(part => !!part).join(' · ');
  }

  getUnplacedDamageCount(): number {
    return this.routingChainUnplacedDamages.reduce((total, damage) => total + damage.count, 0);
  }

  private buildRoutingChainNodes(legs: ComParcel[]): { nodes: RoutingChainNode[]; ends: Map<ComParcel, { from: RoutingChainNode; to: RoutingChainNode }> } {
    const nodes: RoutingChainNode[] = [];
    const ends = new Map<ComParcel, { from: RoutingChainNode; to: RoutingChainNode }>();

    const pushPoint = (point: RoutingChainPoint): RoutingChainNode => {
      const previous = nodes[nodes.length - 1];
      if (previous && this.isSameRoutingPoint(previous.point, point)) {
        return previous;
      }
      const node: RoutingChainNode = { point, damages: [], pickings: [] };
      nodes.push(node);
      return node;
    };
    const undefinedPoint = (): RoutingChainPoint => ({ code: '', name: 'Non definito', kind: 'warehouse' });

    let transportCount = 0;

    legs.forEach((comParcel, index) => {
      const isLast = index === legs.length - 1;
      const status = comParcel.parcelComplementaryStatus || '';

      const fromPoint = this.resolveRoutingChainPoint(comParcel, 'from', false);
      const from = fromPoint
        ? pushPoint(fromPoint)
        : (nodes[nodes.length - 1] || pushPoint(undefinedPoint()));
      const to = pushPoint(this.resolveRoutingChainPoint(comParcel, 'to', isLast) || undefinedPoint());

      to.point.status = status;
      if (from.point.status === undefined) {
        from.point.status = status;
      }

      if (from !== to) {
        const carrierCode = this.resolveLegCarrierCode(comParcel);
        from.leg = {
          sequence: comParcel.parcelComplementarySequence || (this.comParcels.indexOf(comParcel) + 1),
          transport: ++transportCount,
          status,
          carrierCode,
          carrierName: this.getCarrierName(carrierCode)
        };
      }

      ends.set(comParcel, { from, to });
    });

    return { nodes, ends };
  }

  private isMisroutingLeg(comParcel: ComParcel): boolean {
    return (comParcel?.parcelComplementaryType || '').trim().toUpperCase() === 'MISROUTING'
      || (comParcel?.parcelComplementaryStatus || '').trim().toUpperCase() === 'MISROUTING';
  }

  private isOffRouteScan(comParcel: ComParcel): boolean {
    return (comParcel?.parcelComplementaryStatus || '').trim().toUpperCase() === 'MISROUTING'
      && !!comParcel.parcelComplementaryWarehouseFrom
      && comParcel.parcelComplementaryWarehouseFrom === comParcel.parcelComplementaryWarehouseTo;
  }

  private isSameRoutingPoint(a: RoutingChainPoint | null, b: RoutingChainPoint | null): boolean {
    return !!a && !!b && !!a.code && a.code === b.code && a.kind === b.kind;
  }

  private resolveRoutingChainPoint(comParcel: ComParcel, side: 'from' | 'to', isFinalDestination: boolean): RoutingChainPoint | null {
    const dealerCode = side === 'from' ? comParcel.parcelComplementaryDealerFrom : comParcel.parcelComplementaryDealerTo;

    if (isFinalDestination && side === 'to') {
      return {
        code: this.dealer?.dealerCode || dealerCode || 'DEALER',
        name: this.dealer?.dealerName || this.dealer?.dealerLegalName || dealerCode || 'Destinazione finale',
        kind: 'dealer',
        place: this.formatRoutingPlace(this.dealer?.dealerOperatingAddressCity, this.dealer?.dealerOperatingAddressProvince)
      };
    }

    const warehouseCode = side === 'from'
      ? comParcel.parcelComplementaryWarehouseFrom
      : comParcel.parcelComplementaryWarehouseTo;

    if (warehouseCode) {
      const warehouse = this.warehouseMap.get(warehouseCode);
      return {
        code: warehouseCode,
        name: this.getWarehouseName(warehouseCode),
        kind: 'warehouse',
        place: this.formatRoutingPlace(warehouse?.warehouseAddressCity, warehouse?.warehouseAddressProvince)
      };
    }

    return dealerCode ? { code: dealerCode, name: dealerCode, kind: 'dealer' } : null;
  }

  private formatRoutingPlace(city: string | undefined, province: string | undefined): string {
    return [city, province ? `(${province})` : ''].filter(part => !!part).join(' ');
  }

  private resolveLegCarrierCode(comParcel: ComParcel): string | undefined {
    return comParcel.loadingListCarrierCode
      || comParcel.carrierCodeDefaultGroup
      || comParcel.parcelComplementaryCarrierCode
      || comParcel.carrierCode
      || undefined;
  }

  private getCarrierName(carrierCode: string | undefined): string {
    if (!carrierCode) return '';
    const carrier = this.carriers.find(c => c.carrierCode === carrierCode);
    return carrier?.carrierName || carrierCode;
  }

  getRoutingStatusLabel(status: string | undefined | null): string {
    const key = (status || '').trim().toUpperCase();
    return COM_PARCEL_STATUS_LABELS[key] ?? key;
  }

  getRoutingStatusClass(status: string | undefined): string {
    return status ? 'status-' + status.toLowerCase().replace(/_/g, '-') : '';
  }
}
